import { useState, useMemo } from 'react';
import { Badge } from '../shared/components/Badge';
import { Tooltip } from '../shared/components/Tooltip';
import { useProducts } from '../shared/hooks/useProducts';
import { getStatusInfo } from '../shared/utils/inventoryCalculations';
import { ProductWithCalculations, StatusType } from '../shared/types/inventory';
import { ProductDetailModal } from '../components/ProductDetailModal';
import { Loader2, AlertCircle, RefreshCw, Search, ArrowUpDown, ArrowUp, ArrowDown, X } from 'lucide-react';
import { StatusCards } from '../components/StatusCards';

type SortField = 'nome' | 'estoqueAtual' | 'cmd' | 'ss' | 'rop' | 'cobertura' | 'estoqueAlvo' | 'pedidoSugerido' | 'status';
type SortDirection = 'asc' | 'desc';

const STATUS_PRIORITY: Record<StatusType, number> = {
  ruptura: 1,
  iminente: 2,
  minimo: 3,
  critica: 4,
  excesso: 5,
  saudavel: 6,
};

export const TablePage = () => {
  const { products, loading, error, refetch } = useProducts();
  const [selectedProduct, setSelectedProduct] = useState<ProductWithCalculations | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [activeStatus, setActiveStatus] = useState<StatusType | null>(null);

  const statusCounts = useMemo(() => {
    const counts: Record<StatusType, number> = {
      ruptura: 0,
      iminente: 0,
      minimo: 0,
      critica: 0,
      excesso: 0,
      saudavel: 0,
    };
    products.forEach((p) => {
      counts[p.status]++;
    });
    return counts;
  }, [products]);

  const filteredAndSorted = useMemo(() => {
    let result = [...products];

    if (activeStatus) {
      result = result.filter((p) => p.status === activeStatus);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.nome.toLowerCase().includes(q) ||
          p.ean.toLowerCase().includes(q)
      );
    }

    if (sortField) {
      result.sort((a, b) => {
        let valA: number | string;
        let valB: number | string;

        if (sortField === 'nome') {
          valA = a.nome.toLowerCase();
          valB = b.nome.toLowerCase();
        } else if (sortField === 'status') {
          valA = STATUS_PRIORITY[a.status];
          valB = STATUS_PRIORITY[b.status];
        } else {
          valA = a[sortField];
          valB = b[sortField];
        }

        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [products, searchQuery, sortField, sortDirection, activeStatus]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortField(null);
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleStatusClick = (status: StatusType) => {
    setActiveStatus((prev) => (prev === status ? null : status));
  };

  const formatNumber = (num: number, decimals: number = 2) => {
    return num.toFixed(decimals);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3.5 h-3.5 text-gray-400 ml-1 flex-shrink-0" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="w-3.5 h-3.5 text-teal-600 ml-1 flex-shrink-0" />;
    }
    return <ArrowDown className="w-3.5 h-3.5 text-teal-600 ml-1 flex-shrink-0" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
          <p className="text-gray-500 text-sm">Carregando dados...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <p className="text-gray-900 font-semibold">Erro ao carregar dados</p>
          <p className="text-gray-500 text-sm">{error}</p>
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Tabela de Reposição</h1>
          <p className="text-gray-600">
            Analise detalhada dos produtos com sugestoes de pedido baseadas em dados
          </p>
        </div>
        <button
          onClick={refetch}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 shadow-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      <StatusCards
        counts={statusCounts}
        activeStatus={activeStatus}
        onStatusClick={handleStatusClick}
      />

      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome ou EAN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent shadow-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <span className="text-sm text-gray-500">
          {filteredAndSorted.length} de {products.length} produtos
        </span>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  onClick={() => handleSort('nome')}
                >
                  <div className="flex items-center">
                    Produto
                    <SortIcon field="nome" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  onClick={() => handleSort('estoqueAtual')}
                >
                  <div className="flex items-center justify-end">
                    <Tooltip content="Quantidade atual em estoque">
                      <span className="cursor-help">Estoque Atual</span>
                    </Tooltip>
                    <SortIcon field="estoqueAtual" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  onClick={() => handleSort('cmd')}
                >
                  <div className="flex items-center justify-end">
                    <Tooltip content="Consumo Médio Diário: Vendas no Período / Dias do Período">
                      <span className="cursor-help">CMD</span>
                    </Tooltip>
                    <SortIcon field="cmd" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  onClick={() => handleSort('ss')}
                >
                  <div className="flex items-center justify-end">
                    <Tooltip content="Safety Stock: Estoque de Segurança = CMD x Lead Time">
                      <span className="cursor-help">SS</span>
                    </Tooltip>
                    <SortIcon field="ss" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  onClick={() => handleSort('rop')}
                >
                  <div className="flex items-center justify-end">
                    <Tooltip content="Reorder Point: Ponto de Pedido = DDLT + SS">
                      <span className="cursor-help">ROP</span>
                    </Tooltip>
                    <SortIcon field="rop" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  onClick={() => handleSort('cobertura')}
                >
                  <div className="flex items-center justify-end">
                    <Tooltip content="Dias de cobertura: Estoque Atual / CMD">
                      <span className="cursor-help">Cobertura (dias)</span>
                    </Tooltip>
                    <SortIcon field="cobertura" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  onClick={() => handleSort('estoqueAlvo')}
                >
                  <div className="flex items-center justify-end">
                    <Tooltip content="Nível ideal de estoque: DDLT + SS + (Cobertura Alvo x CMD)">
                      <span className="cursor-help">Estoque Alvo</span>
                    </Tooltip>
                    <SortIcon field="estoqueAlvo" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  onClick={() => handleSort('pedidoSugerido')}
                >
                  <div className="flex items-center justify-end">
                    <Tooltip content="Quantidade sugerida para reposicao: MAX(0, Estoque Alvo - Estoque Atual)">
                      <span className="cursor-help">Pedido Sugerido</span>
                    </Tooltip>
                    <SortIcon field="pedidoSugerido" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center">
                    Status
                    <SortIcon field="status" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredAndSorted.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Search className="w-8 h-8 text-gray-300" />
                      <p className="text-gray-500 font-medium">Nenhum produto encontrado</p>
                      <p className="text-gray-400 text-sm">Tente ajustar a busca ou os filtros</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAndSorted.map((product) => {
                  const statusInfo = getStatusInfo(product.status);
                  return (
                    <tr
                      key={product.id}
                      className="hover:bg-blue-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedProduct(product)}
                    >
                      <td className="px-4 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{product.nome}</div>
                          <div className="text-sm text-gray-500">EAN: {product.ean}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right font-medium text-gray-900">
                        {product.estoqueAtual}
                      </td>
                      <td className="px-4 py-4 text-right text-gray-900">
                        {formatNumber(product.cmd)}
                      </td>
                      <td className="px-4 py-4 text-right text-gray-900">
                        {formatNumber(product.ss, 0)}
                      </td>
                      <td className="px-4 py-4 text-right text-gray-900">
                        {formatNumber(product.rop, 0)}
                      </td>
                      <td className="px-4 py-4 text-right text-gray-900">
                        {formatNumber(product.cobertura, 0)}
                      </td>
                      <td className="px-4 py-4 text-right text-gray-900">
                        {formatNumber(product.estoqueAlvo, 0)}
                      </td>
                      <td className="px-4 py-4 text-right font-semibold text-blue-600">
                        {formatNumber(product.pedidoSugerido, 0)}
                      </td>
                      <td className="px-4 py-4">
                        <Badge
                          label={statusInfo.label}
                          color={statusInfo.color}
                          bgColor={statusInfo.bgColor}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>Dica:</strong> Passe o mouse sobre os títulos das colunas para ver a explicação de cada métrica. Clique em qualquer produto para ver a visualização detalhada. Clique nos cabeçalhos para ordenar.
        </p>
      </div>

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
};

